import SquareIcon from "@mui/icons-material/Square";
import React, {useEffect, useRef, useState} from "react";
import {HourglassEmpty} from "@mui/icons-material";
import Box from "@mui/material/Box";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StopIcon from "@mui/icons-material/Stop";
import ReplayIcon from "@mui/icons-material/Replay";

export const RecordingStateDisplay = React.memo(({
                                                     recordingInProgress,
                                                     localRecordingInProgress,
                                                 }) => {
    const [recordTime, setRecordTime] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        if (recordingInProgress) {
            timerRef.current = setInterval(() => {
                setRecordTime(prevTime => prevTime + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
            setRecordTime(0);
        }

        return () => clearInterval(timerRef.current);
    }, [recordingInProgress]);

    const buttonStyle = {
        background: recordingInProgress || localRecordingInProgress ? "#000" : "#E3E5E8",
        color: "white",
        width: "120px",
        height: "40px",
        borderRadius: "6px",
        display: 'flex', // Ensure flexbox is used
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: "8px",
        cursor: "not-allowed",
    }

    const ButtonIcon = () => {
        const iconStyle = recordingInProgress || localRecordingInProgress
            ? {
                color: '#F23000',
                animation: 'flashing 1s infinite'
            }
            : {
                color: '#F23000'
            };


        if (recordingInProgress) {
            return (
                <StopIcon style={iconStyle}/>
            );
        }

        if (localRecordingInProgress) {
            return (
                <HourglassEmpty/>
            );
        }

        return (
            <FiberManualRecordIcon style={{color: '#FFFFFF'}}/>
        );
    }

    const formatTime = ({recordTime}) => {
        const hours = Math.floor(recordTime / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((recordTime % 3600) / 60).toString().padStart(2, '0');
        const seconds = (recordTime % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const ButtonText = () => {
        if (recordingInProgress) {
            return (
                <Box style={textStyle}>
                    {formatTime({recordTime})}
                </Box>
            );
        }

        if (localRecordingInProgress) {
            return (
                <Box style={textStyle}>
                    Starting
                </Box>
            );
        }

        return (
            <Box style={textStyle}>
                {formatTime({recordTime})}
            </Box>
        );
    }

    const textStyle = {
        fontFamily: "Inter",
        fontSize: "12px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "16px",
        letterSpacing: "0.14px",
        color: recordingInProgress || localRecordingInProgress ? "white" : "#1a1a1a",
        // disable highlight on double click
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        KhtmlUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
    }

    const textContainerStyle = {
        width: "60px",
        cursor: "pointer",
    }

    return (
        <>
            <style>
                {`
                    @keyframes flashing {
                        0% { opacity: 1; }
                        50% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
            {<Box sx={buttonStyle}>
                {<ButtonIcon/>}
                <Box style={textContainerStyle}>{ButtonText()}</Box>
            </Box>}
        </>);
});

const NewRecordButton = React.memo(({
                                        recordingInProgress,
                                        localRecordingInProgress,
                                        startRecording,
                                        stopRecording,
                                        recordingPerformed,
                                        onStartRecording,
    disabled,
                                    }) => {
    const [recordTime, setRecordTime] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        if (recordingInProgress) {
            timerRef.current = setInterval(() => {
                setRecordTime(prevTime => prevTime + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
            setRecordTime(0);
        }

        return () => clearInterval(timerRef.current);
    }, [recordingInProgress]);

    const buttonStyle = (recordingPerformed) => {
        return ({
            backgroundColor: recordingInProgress || localRecordingInProgress ? "#000" : recordingPerformed ? "#E3E5E8" : disabled ? "#E0B0A0" : "#F23000",
            color: recordingPerformed ? "#1a1a1a" : "white",
            width: "120px",
            height: "40px",
            borderRadius: "6px",
            // Padding 24Px on left and right, and 14px on top and bottom
            // padding: "24px 14px",
            display: 'flex', // Ensure flexbox is used
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: "8px",
            cursor: disabled ? 'not-allowed' : 'pointer',
        })
    }

    const ButtonIcon = () => {
        const iconStyle = recordingInProgress || localRecordingInProgress
            ? {
                color: '#F23000',
                animation: 'flashing 1s infinite'
            }
            : {
                color: '#F23000'
            };


        if (recordingInProgress) {
            return (
                <StopIcon style={iconStyle}/>
            );
        }

        if (localRecordingInProgress) {
            return (
                <HourglassEmpty/>
            );
        }

        return (
            <FiberManualRecordIcon style={{color: '#FFFFFF'}}/>
        );
    }

    const handleButtonClick = async () => {

        if (localRecordingInProgress && !recordingInProgress) return; // Prevents button action if disabled

        if (recordingPerformed) {
            // Refresh the page
            window.location.reload();
            return;
        }

        if (recordingInProgress) {
            await stopRecording();
        } else if (!disabled) { // Disabled is only to stop the start of a recording.
            onStartRecording?.();
            await startRecording();
        }
    }

    const formatTime = ({recordTime}) => {
        const hours = Math.floor(recordTime / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((recordTime % 3600) / 60).toString().padStart(2, '0');
        const seconds = (recordTime % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const ButtonText = () => {
        if (recordingInProgress) {
            return (
                <Box style={textStyle}>
                    {formatTime({recordTime})}
                </Box>
            );
        }

        if (localRecordingInProgress) {
            return (
                <Box style={textStyle}>
                    Starting
                </Box>
            );
        }

        if (recordingPerformed) {
            return (
                <Box style={textStyle}>
                    New Recording
                </Box>
            );
        }

        return (
            <Box style={textStyle}>
                Record
            </Box>
        );
    }

    const textStyle = {
        fontFamily: "Inter",
        fontStyle: "normal",
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "16px",
        letterSpacing: "0.14px",
        color: recordingPerformed ? "#1a1a1a" : "white",
        // disable highlight on double click
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        KhtmlUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
    }

    const textContainerStyle = {
        width: "60px",
        cursor: disabled ? 'not-allowed' : 'pointer',
    }

    return (
        <>
            <style>
                {`
                    @keyframes flashing {
                        0% { opacity: 1; }
                        50% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
            {<Box onClick={handleButtonClick} sx={buttonStyle(recordingPerformed)}>
                {!recordingPerformed && <ButtonIcon/>}
                <Box style={textContainerStyle}>{ButtonText()}</Box>
            </Box>}
        </>);
});

export default NewRecordButton;
